/**
 * Copyright 2024 Design Barn Inc.
 */

import type CleverTapType from 'clevertap-web-sdk';

type Region = 'sg1' | 'in1' | 'us1' | 'aps3' | 'mec1' | 'eu1' | 'sk1';

// Read configuration from environment variables
const CLEVERTAP_ACCOUNT_ID = process.env['NEXT_PUBLIC_CLEVERTAP_ACCOUNT_ID'] || '';
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
const CLEVERTAP_REGION: Region = (process.env['NEXT_PUBLIC_CLEVERTAP_REGION'] as Region) || 'eu1';

let isInitialized = false;
let clevertapInstance: typeof CleverTapType | null = null;

// Promise-based guards to prevent race conditions on concurrent calls
let importPromise: Promise<typeof CleverTapType | null> | null = null;
let initPromise: Promise<void> | null = null;

// --- Debug logging ---
// Enable in deployed environments via: localStorage.setItem('CT_DEBUG', 'true')
// Checked on every call so you can toggle it at runtime without refreshing.
export function ctLog(...args: unknown[]): void {
  if (
    typeof window !== 'undefined' &&
    (process.env.NODE_ENV === 'development' || localStorage.getItem('CT_DEBUG') === 'true')
  ) {
    // eslint-disable-next-line no-console
    console.log('[CleverTap]', ...args);
  }
}

/**
 * All known Clevertap localStorage/cookie keys:
 * - WZRK_G: GCOOKIE - device/user GUID
 * - WZRK_K: KCOOKIE - user identity info
 * - WZRK_X: LRU_CACHE - maps identities to GUIDs (IMPORTANT!)
 * - WZRK_L: LCOOKIE - backup events
 * - WZRK_S: Session cookie prefix
 * - WZRK_EV: Event cookie
 * - WZRK_META: Meta cookie
 * - WZRK_PR: PR cookie
 * - WZRK_ARP: ARP cookie
 * - WZRK_CAMP: Campaign cookie
 * - WZRK_CAMP_G: Campaign GUID cookie
 * - WZRK_CHARGED_ID: Charged ID cookie
 * - WZRK_WPR: Web push
 * - WZRK_ACCOUNT_ID: Account ID
 * - WZRK_INBOX: Web inbox
 * - WZRK_INBOX_CONFIG: Inbox config
 * - WZRK_PE: Variables
 * - WZRK_PUSH_CONFIG: Push config
 */

/**
 * Clear ALL Clevertap storage (localStorage and cookies).
 * WARNING: This should only be used for consent revocation teardown, NOT for
 * identity switching. For identity switching, use resetClevertapIdentity() instead.
 *
 * Clearing storage manually while the SDK is loaded creates a mismatch between
 * localStorage and the SDK's in-memory LRU cache, which causes onUserLogin.push()
 * to silently skip profile pushes.
 */
export function clearAllClevertapStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  ctLog('clearAllClevertapStorage: clearing all WZRK_* keys and cookies');

  // Clear all WZRK_ keys from localStorage
  const allKeys = Object.keys(localStorage);

  allKeys.forEach((key) => {
    if (key.startsWith('WZRK_') || key.startsWith('wzrk_')) {
      localStorage.removeItem(key);
    }
  });

  // Clear cookies by setting them to expire in the past
  const cookieKeys = ['WZRK_G', 'WZRK_K', 'WZRK_S', 'WZRK_CAMP', 'WZRK_ARP'];

  cookieKeys.forEach((key) => {
    // Clear for current domain
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // Clear for parent domain (e.g., .lottiefiles.com)
    const domain = window.location.hostname.split('.').slice(-2).join('.');

    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain}`;
  });

  // Reset our internal state
  isInitialized = false;
  clevertapInstance = null;
  importPromise = null;
  initPromise = null;
}

/**
 * Get the CleverTap SDK instance (dynamically imported to avoid SSR issues).
 * The SDK references HTMLElement at module evaluation time, so it cannot be
 * statically imported in a Node.js / SSR context.
 * Uses a promise-based singleton to prevent race conditions when called concurrently.
 */
async function getClevertapSdk(): Promise<typeof CleverTapType | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  if (clevertapInstance) {
    return clevertapInstance;
  }

  // Use promise as singleton guard — concurrent calls await the same import
  if (!importPromise) {
    importPromise = (async (): Promise<typeof CleverTapType | null> => {
      try {
        const module = await import(/* webpackMode: "eager" */ 'clevertap-web-sdk');

        clevertapInstance = module.default;
        ctLog('SDK module loaded successfully');

        return clevertapInstance;
      } catch (error) {
        ctLog('SDK module import failed:', error);
        // Allow retry on failure
        importPromise = null;

        return null;
      }
    })();
  }

  return importPromise;
}

/**
 * Initialize CleverTap SDK.
 * Should be called when needed (not on app load - we need consent first).
 * Uses a promise-based lock to prevent double-initialization from concurrent calls.
 */
export async function initCleverTap(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  if (isInitialized) {
    ctLog('initCleverTap: already initialized, skipping');

    return;
  }

  // Return existing init promise if one is in-flight (prevents double-init)
  if (initPromise) {
    ctLog('initCleverTap: initialization already in progress, awaiting');
    await initPromise;

    return;
  }

  if (!CLEVERTAP_ACCOUNT_ID) {
    ctLog('initCleverTap: no account ID configured, skipping');

    return;
  }

  initPromise = (async (): Promise<void> => {
    try {
      ctLog('initCleverTap: starting initialization');
      const sdk = await getClevertapSdk();

      if (!sdk) {
        ctLog('initCleverTap: SDK not available (SSR)');
        initPromise = null;

        return;
      }

      // Initialize with account ID and region
      sdk.init(CLEVERTAP_ACCOUNT_ID, CLEVERTAP_REGION);
      ctLog('initCleverTap: SDK initialized with', CLEVERTAP_ACCOUNT_ID, CLEVERTAP_REGION);

      // Enable SPA mode for NextJS
      sdk.spa = true;

      // Set privacy options
      sdk.privacy.push({ optOut: false });
      sdk.privacy.push({ useIP: true });

      isInitialized = true;
      ctLog('initCleverTap: initialization complete');
    } catch (error) {
      ctLog('initCleverTap: initialization failed:', error);
      // Allow retry on failure
      initPromise = null;
    }
  })();

  await initPromise;
}

/**
 * Reset CleverTap identity state for a fresh user identification.
 *
 * Uses the SDK's native clear() method to ensure both in-memory state ($ct.LRU_CACHE)
 * and localStorage are reset consistently. This prevents the race condition where
 * manual localStorage clearing leaves stale data in the in-memory cache, causing
 * onUserLogin.push() to silently skip the profile push (sendOULFlag = false).
 *
 * Call this AFTER initCleverTap() and BEFORE loginUser().
 */
export async function resetClevertapIdentity(): Promise<void> {
  if (!isInitialized) {
    ctLog('resetClevertapIdentity: not initialized, skipping');

    return;
  }

  const sdk = await getClevertapSdk();

  if (!sdk) {
    return;
  }

  ctLog('resetClevertapIdentity: calling SDK clear() to reset identity state');
  sdk.clear();
}

/**
 * Get the CleverTap instance for direct access if needed.
 * Returns null if SDK is not loaded or during SSR.
 */
export async function getCleverTap(): Promise<typeof CleverTapType | null> {
  return getClevertapSdk();
}

/**
 * Get the current CleverTap ID.
 * Returns the GUID that CleverTap uses to identify the current user/device.
 */
export async function getCleverTapId(): Promise<string | null> {
  if (!isInitialized) {
    return null;
  }

  const sdk = await getClevertapSdk();

  if (!sdk) {
    return null;
  }

  return sdk.getCleverTapID();
}

/**
 * Clevertap user profile interface
 * Based on the viewer query fields
 */
export interface ClevertapUserProfile {
  Email?: string;
  Identity: string;
  Name?: string;
  Photo?: string;
  behance?: string;
  city?: string;
  country?: string;
  dribbble?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  email_verified?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  experiment_cohort?: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  first_name?: string;
  github?: string;
  instagram?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  is_hire?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  journey_status?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  last_name?: string;
  linkedin?: string;
  location?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  mautic_overlap?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  profile_url?: string;
  segments?: string[];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  test_group?: string;
  twitter?: string;
  uuid?: string;
  website?: string;
}

/**
 * Push a user profile to Clevertap via onUserLogin.
 * This creates or updates the user profile and identifies them.
 */
export async function loginUser(profile: ClevertapUserProfile): Promise<void> {
  if (!isInitialized) {
    ctLog('loginUser: not initialized, skipping');

    return;
  }

  const sdk = await getClevertapSdk();

  if (!sdk) {
    return;
  }

  // Build Site object for onUserLogin
  // Filter out undefined values
  const siteData: Record<string, string | number | boolean | Date | null | undefined> = {};

  Object.entries(profile).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // Handle arrays by converting to string (CleverTap SiteData doesn't support arrays directly)
      if (Array.isArray(value)) {
        siteData[key] = value.join(', ');
      } else {
        siteData[key] = value;
      }
    }
  });

  const clevertapProfile = { Site: siteData };

  ctLog('loginUser: pushing profile via onUserLogin', clevertapProfile);

  try {
    sdk.onUserLogin.push(clevertapProfile);
    ctLog('loginUser: onUserLogin.push() completed');
  } catch (error) {
    ctLog('loginUser: onUserLogin.push() failed:', error);
  }
}

/**
 * Helper to read stored profile data from localStorage
 */
function getStoredProfileData(): Record<string, unknown> {
  if (typeof window === 'undefined') {
    return {};
  }

  const data: Record<string, unknown> = {};

  // Try to read the K cookie which contains identity info
  const kData = localStorage.getItem('WZRK_K');

  if (kData) {
    try {
      data['WZRK_K'] = JSON.parse(kData);
    } catch {
      data['WZRK_K'] = kData;
    }
  }

  // Try to read the G cookie which contains the GUID
  const gData = localStorage.getItem('WZRK_G');

  if (gData) {
    data['WZRK_G'] = gData;
  }

  // Try to read the LRU cache
  const xData = localStorage.getItem('WZRK_X');

  if (xData) {
    try {
      data['WZRK_X'] = JSON.parse(xData);
    } catch {
      data['WZRK_X'] = xData;
    }
  }

  return data;
}

/**
 * Get the current user profile from Clevertap.
 * Note: This returns what we can access from the SDK, not the full server profile.
 */
export async function getCurrentProfile(): Promise<Record<string, unknown> | null> {
  if (!isInitialized) {
    return null;
  }

  const sdk = await getClevertapSdk();

  if (!sdk) {
    return null;
  }

  // Get what we can from the SDK
  const ctId = sdk.getCleverTapID();
  const accountId = sdk.getAccountID();

  const profileInfo = {
    clevertapId: ctId,
    accountId,
    // The SDK doesn't expose a direct way to get the full profile
    // But we can read from localStorage what was stored
    storedData: getStoredProfileData(),
  };

  return profileInfo;
}

/**
 * Push a sign-up event to Clevertap
 */
export async function pushSignUpEvent(userId: string): Promise<void> {
  if (!isInitialized) {
    ctLog('pushSignUpEvent: not initialized, skipping');

    return;
  }

  const sdk = await getClevertapSdk();

  if (!sdk) {
    return;
  }

  const eventData = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    user_id: userId,
    source: 'web',
    timestamp: new Date().toISOString(),
  };

  ctLog('pushSignUpEvent: pushing sign-up event', eventData);

  try {
    sdk.event.push('sign-up', eventData);
    ctLog('pushSignUpEvent: event pushed successfully');
  } catch (error) {
    ctLog('pushSignUpEvent: event push failed:', error);
  }
}
